import React, { useEffect, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Grid,
  List,
  X,
  Columns,
} from "lucide-react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../contexts/ThemeContext";

const CalendarHeader = ({
  currentDate,
  calendarView,
  onViewChange,
  onNavigate,
  onGoToToday,
  trainers,
  selectedTrainerId,
  onTrainerChange,
  displayMode,
  onDisplayModeChange,
  customDateRange,
  onCustomDateChange,
  totalSessions,
}) => {
  const { colors } = useTheme();
  const { t, i18n } = useTranslation();

  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  const getDateTitle = () => {
    // Custom Date Range Title for List View
    if (
      displayMode === "list" &&
      customDateRange?.startDate &&
      customDateRange?.endDate
    ) {
      const start = moment(customDateRange.startDate).locale(i18n.language);
      const end = moment(customDateRange.endDate).locale(i18n.language);

      if (start.year() === end.year()) {
        return `${start.format("D MMM")} - ${end.format("D MMM, YYYY")}`;
      }
      return `${start.format("D MMM, YYYY")} - ${end.format("D MMM, YYYY")}`;
    }

    // Create a new moment object and explicitly set the locale to the current language
    const localDate = moment(currentDate).locale(i18n.language);

    switch (calendarView) {
      case "daily":
        return localDate.format("dddd, MMMM D, YYYY");
      case "three_day": {
        const start3 = moment(currentDate).locale(i18n.language);
        const end3 = moment(currentDate).locale(i18n.language).add(2, "days");
        return `${start3.format("D MMM")} - ${end3.format("D MMM, YYYY")}`;
      }
      case "weekly":
        const weekStart = moment(currentDate)
          .locale(i18n.language)
          .startOf("week");
        const weekEnd = moment(currentDate).locale(i18n.language).endOf("week");
        return `${weekStart.format("D MMM")} - ${weekEnd.format("D MMM, YYYY")}`;
      case "monthly":
      default:
        return localDate.format("MMMM YYYY");
    }
  };

  const getNavigationLabel = () => {
    switch (calendarView) {
      case "daily":
        return t("sessionsPage.calendar.views.day").toLowerCase();
      case "three_day":
        return "3 gün";
      case "weekly":
        return t("sessionsPage.calendar.views.week").toLowerCase();
      case "monthly":
      default:
        return t("sessionsPage.calendar.views.month").toLowerCase();
    }
  };

  const viewOptions = [
    {
      key: "monthly",
      label: t("sessionsPage.calendar.views.month"),
      icon: Grid,
    },
    {
      key: "weekly",
      label: t("sessionsPage.calendar.views.week"),
      icon: Calendar,
    },
    {
      key: "three_day",
      label: "3 " + t("sessionsPage.calendar.views.day"),
      icon: Columns,
    },
    { key: "daily", label: t("sessionsPage.calendar.views.day"), icon: List },
  ];

  const displayModes = [
    {
      key: "calendar",
      label: t("sessionsPage.calendar.modes.calendar"),
      icon: Calendar,
    },
    { key: "list", label: t("sessionsPage.calendar.modes.list"), icon: List },
  ];

  return (
    <div className="mb-3 md:mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0">
        {/* Left side - Date navigation */}
        <div className="flex items-center gap-2 md:space-x-4">
          <div className="flex items-center gap-2 md:space-x-3 min-w-0">
            <h2
              className={`text-sm md:text-xl font-bold ${colors.textPrimary} truncate`}
            >
              {getDateTitle()}
            </h2>
            <div
              className={`flex items-center space-x-1 ${colors.paginationBg} rounded-xl p-1`}
            >
              <button
                onClick={() => onNavigate(-1)}
                className={`p-2 rounded-lg ${colors.paginationHover} transition-all duration-200`}
                title={`${t("sessionsPage.calendar.previous")} ${getNavigationLabel()}`}
              >
                <ChevronLeft className={`h-4 w-4 ${colors.textSecondary}`} />
              </button>
              <button
                onClick={() => onNavigate(1)}
                className={`p-2 rounded-lg ${colors.paginationHover} transition-all duration-200`}
                title={`${t("sessionsPage.calendar.next")} ${getNavigationLabel()}`}
              >
                <ChevronRight className={`h-4 w-4 ${colors.textSecondary}`} />
              </button>
            </div>
            <button
              onClick={onGoToToday}
              className={`px-2.5 md:px-4 py-1.5 md:py-2 text-xs md:text-sm ${colors.btnSecondaryBg} ${colors.btnSecondaryText} rounded-lg md:rounded-xl ${colors.btnSecondaryHover} transition-all duration-200 font-semibold shadow-sm whitespace-nowrap flex-shrink-0`}
            >
              {t("sessionsPage.calendar.today")}
            </button>
          </div>
        </div>

        {/* Right side - Display mode toggle and View selector */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Display Mode Toggle */}
          <div className="calendar-view-toggle">
            {displayModes.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => onDisplayModeChange(key)}
                className={`calendar-view-btn ${
                  displayMode === key
                    ? "calendar-view-btn-active"
                    : `${colors.textTertiary} calendar-view-btn-inactive`
                }`}
                title={`${label} ${t("common.viewDetails", { defaultValue: "view" })}`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          {/* Custom Date Range (List View Only) */}
          {displayMode === "list" && (
            <div
              className={`flex items-center gap-2 bg-gray-100 dark:bg-black/40 rounded-xl p-1.5 md:p-2 shadow-inner flex-shrink-0`}
            >
              {/* Start Date */}
              <div
                onClick={() => {
                  try { startDateRef.current?.showPicker?.(); } catch (e) {}
                }}
                className={`calendar-date-picker ${colors.bgHover} ${colors.border}`}
              >
                <Calendar
                  className={`w-3.5 h-3.5 ${colors.textSecondary} mr-1.5 md:mr-2 flex-shrink-0`}
                />
                <span
                  className={`text-[10px] md:text-xs ${customDateRange?.startDate ? colors.textPrimary : colors.textTertiary} truncate font-medium`}
                >
                  {customDateRange?.startDate
                    ? moment(customDateRange.startDate).format("DD MMM YYYY")
                    : "Başlangıç"}
                </span>
                <input
                  ref={startDateRef}
                  type="date"
                  className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                  onChange={(e) =>
                    onCustomDateChange((prev) => ({
                      ...prev,
                      startDate: e.target.value
                        ? new Date(e.target.value)
                        : null,
                    }))
                  }
                  value={
                    customDateRange?.startDate
                      ? moment(customDateRange.startDate).format("YYYY-MM-DD")
                      : ""
                  }
                />
              </div>

              <span className={`${colors.textSecondary} text-xs font-bold`}>
                -
              </span>

              {/* End Date */}
              <div
                onClick={() => {
                  try { endDateRef.current?.showPicker?.(); } catch (e) {}
                }}
                className={`calendar-date-picker ${colors.bgHover} ${colors.border}`}
              >
                <Calendar
                  className={`w-3.5 h-3.5 ${colors.textSecondary} mr-1.5 md:mr-2 flex-shrink-0`}
                />
                <span
                  className={`text-[10px] md:text-xs ${customDateRange?.endDate ? colors.textPrimary : colors.textTertiary} truncate font-medium`}
                >
                  {customDateRange?.endDate
                    ? moment(customDateRange.endDate).format("DD MMM YYYY")
                    : "Bitiş"}
                </span>
                <input
                  ref={endDateRef}
                  type="date"
                  className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                  onChange={(e) =>
                    onCustomDateChange((prev) => ({
                      ...prev,
                      endDate: e.target.value ? new Date(e.target.value) : null,
                    }))
                  }
                  min={
                    customDateRange?.startDate
                      ? moment(customDateRange.startDate).format("YYYY-MM-DD")
                      : undefined
                  }
                  value={
                    customDateRange?.endDate
                      ? moment(customDateRange.endDate).format("YYYY-MM-DD")
                      : ""
                  }
                />
              </div>

              {(customDateRange?.startDate || customDateRange?.endDate) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCustomDateChange({ startDate: null, endDate: null });
                  }}
                  className="ml-1 hover:bg-white/10 p-1.5 rounded-full text-red-400 transition-colors flex-shrink-0"
                  title="Temizle"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}

          {/* View selector (Hidden in List View to improve responsiveness) */}
          {displayMode !== "list" && (
            <div
              className={`flex items-center bg-gray-100 dark:bg-black/40 rounded-xl p-1 shadow-inner flex-shrink-0`}
            >
              {viewOptions.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => onViewChange(key)}
                  className={`flex items-center gap-1.5 px-2.5 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-bold transition-all duration-200 whitespace-nowrap ${
                    calendarView === key
                      ? "bg-indigo-600 text-white shadow-sm"
                      : `${colors.textTertiary} hover:text-indigo-400 hover:${colors.bgHoverStrong}`
                  }`}
                  title={label}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* List mode info */}
      {displayMode === "list" && (
        <div className={`mt-6 pt-6 border-t ${colors.tableBorder}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-sm font-semibold ${colors.textPrimary}`}>
                Toplam Seans Sayısı: {totalSessions || 0}
              </h3>
              <p className={`text-xs ${colors.textTertiary} mt-1`}>
                {customDateRange?.startDate && customDateRange?.endDate ? (
                  `${moment(customDateRange.startDate).format("D MMMM")} - ${moment(customDateRange.endDate).format("D MMMM YYYY")} tarihleri arasındaki seanslar`
                ) : (
                  <>
                    {calendarView === "daily" &&
                      t("sessionsPage.calendar.listDescriptions.daily", {
                        date: moment(currentDate)
                          .locale(i18n.language)
                          .format("DD MMMM YYYY"),
                      })}
                    {calendarView === "weekly" &&
                      t("sessionsPage.calendar.listDescriptions.weekly", {
                        start: moment(currentDate)
                          .locale(i18n.language)
                          .startOf("week")
                          .format("DD MMM"),
                        end: moment(currentDate)
                          .locale(i18n.language)
                          .endOf("week")
                          .format("DD MMM YYYY"),
                      })}
                    {calendarView === "monthly" &&
                      t("sessionsPage.calendar.listDescriptions.monthly", {
                        date: moment(currentDate)
                          .locale(i18n.language)
                          .format("MMMM YYYY"),
                      })}
                  </>
                )}
              </p>
            </div>

            {/* Quick stats for list view */}
            <div className="text-right">
              <div className={`text-xs ${colors.textMuted}`}>
                {t("sessionsPage.calendar.selectedPeriod")}
              </div>
              <div className={`text-sm font-semibold ${colors.textPrimary}`}>
                {getDateTitle()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarHeader;
